import { useState } from "react";
import { homeService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { sortMaterialResponse } from "@miplatform/utils";

export const useImportPlanAreaViewModel = ({
  area,
  stateManager,
  planDetails,
  setPlanDetails,
  setCheckedAreas,
}) => {
  const [materials, setMaterials] = useState([]);

  const getMaterials = () => {
    stateManager.run(async () => {
      const planAreaMaterialId =
        area?.materials?.map((m) => m.materialId) ?? [];
      const response = await homeService.materials.getAll({
        filters: { id: { in: { type: "guid", value: planAreaMaterialId } } },
      });
      if (response.status === HTTP_STATUSES.OK) {
        const mappedData = area?.materials?.map((material) => {
          const materialDetails = response.data.find(
            (item) => item.id === material.materialId
          );
          return { ...materialDetails, ...material };
        });
        const displayOrderSortedData = sortMaterialResponse(mappedData);
        setMaterials(displayOrderSortedData);
      }
    });
  };

  const handleMarkMaterialAsViewed = (materialId) => {
    setPlanDetails((prevPlanDetails) => {
      return {
        ...prevPlanDetails,
        planAreas: prevPlanDetails.planAreas.map((planArea) =>
          planArea.id === area.id
            ? {
                ...planArea,
                materials: planArea.materials.map((material) =>
                  material.id === materialId
                    ? { ...material, isViewed: !material.isViewed }
                    : material
                ),
              }
            : planArea
        ),
      };
    });
  };

  const handleMarkAreaAsReviewed = () => {
    const updatedPlanDetails = {
      ...planDetails,
      planAreas: planDetails.planAreas.map((planArea) =>
        planArea.id === area.id
          ? { ...planArea, floor: undefined, checked: true }
          : planArea
      ),
    };
    const updatedArea = updatedPlanDetails.planAreas.find(
      (planArea) => planArea.id === area.id
    );
    stateManager.run(async () => {
      const response = await homeService.plans.importPlanReviewed({
        data: updatedArea,
        id: planDetails.planId,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setPlanDetails(updatedPlanDetails);
      }
    });
  };

  const handleAddRemovalReason = (materialId, reason) => {
    const updatedPlanDetails = {
      ...planDetails,
      planAreas: planDetails.planAreas.map((planArea) =>
        planArea.id === area.id
          ? {
              ...planArea,
              materials: planArea.materials.map((material) =>
                material.id === materialId
                  ? {
                      ...material,
                      removalReason: reason,
                      planConfigStatus: "IsRemoved",
                    }
                  : material
              ),
            }
          : planArea
      ),
    };
    setPlanDetails(updatedPlanDetails);
  };

  // helper for selecting a area
  const checkArea = () => {
    const areaToAdd = planDetails.planAreas.find(
      (planArea) => planArea.id === area.id
    );
    if (areaToAdd) {
      setCheckedAreas([areaToAdd]);
    }
  };

  const handleMarkMaterialAsRemoved = (materialId) => {
    const updatedPlanDetails = {
      ...planDetails,
      planAreas: planDetails.planAreas.map((planArea) =>
        planArea.id === area.id
          ? {
              ...planArea,
              materials: planArea.materials.map((material) =>
                material.id === materialId
                  ? { ...material, planConfigStatus: "IsRemoved" }
                  : material
              ),
            }
          : planArea
      ),
    };

    checkArea();
    setPlanDetails(updatedPlanDetails);
  };

  const handleReplaceRequiredMaterial = (materialId) => {
    const updatedPlanDetails = {
      ...planDetails,
      planAreas: planDetails.planAreas.map((planArea) =>
        planArea.id === area.id
          ? {
              ...planArea,
              materials: planArea.materials.map((material) =>
                material.id === materialId
                  ? {
                      ...material,
                      planConfigStatus: "IsRemoved",
                      removalReason: null,
                    }
                  : material
              ),
            }
          : planArea
      ),
    };

    checkArea();
    setPlanDetails(updatedPlanDetails);
  };

  const handleUndoRemovedMaterial = (materialId, referenceId) => {
    const updatedPlanDetails = {
      ...planDetails,
      planAreas: planDetails.planAreas.map((planArea) =>
        planArea.id === area.id
          ? {
              ...planArea,
              materials: planArea.materials
                .filter((material) => {
                  if (
                    material.planConfigStatus === "IsAdded" &&
                    material.referenceId === referenceId
                  ) {
                    return false;
                  }
                  return true;
                })
                .map((material) => {
                  if (material.id === materialId) {
                    return { ...material, planConfigStatus: null };
                  }
                  return material;
                }),
            }
          : planArea
      ),
    };

    checkArea();
    setPlanDetails(updatedPlanDetails);
  };

  const handleUpdateMaterialQuantity = (materialId, quantity) => {
    const updatedPlanDetails = {
      ...planDetails,
      planAreas: planDetails.planAreas.map((planArea) =>
        planArea.id === area.id
          ? {
              ...planArea,
              materials: planArea.materials.map((material) =>
                material.id === materialId
                  ? { ...material, quantity: Number(quantity) }
                  : material
              ),
            }
          : planArea
      ),
    };

    setPlanDetails(updatedPlanDetails);
  };

  // helper for removing material
  const removeMaterial = (materialId) => {
    const updatedPlanDetails = {
      ...planDetails,
      planAreas: planDetails.planAreas.map((planArea) =>
        planArea.id === area.id
          ? {
              ...planArea,
              materials: planArea.materials.filter(
                (material) => material.id !== materialId
              ),
            }
          : planArea
      ),
    };

    checkArea();
    setPlanDetails(updatedPlanDetails);
  };

  const handleRemoveOptionalMaterial = (materialId) => {
    removeMaterial(materialId);
  };

  const handleRemoveAddedMaterial = (materialId) => {
    removeMaterial(materialId);
  };

  return {
    isLoading: stateManager.isBusy(),
    materials,
    getMaterials,
    setMaterials,
    handleMarkMaterialAsViewed,
    handleMarkAreaAsReviewed,
    handleAddRemovalReason,
    handleMarkMaterialAsRemoved,
    handleRemoveOptionalMaterial,
    handleReplaceRequiredMaterial,
    handleUndoRemovedMaterial,
    handleRemoveAddedMaterial,
    handleUpdateMaterialQuantity,
    checkArea,
  };
};
